// src/components/Header.tsx - Clean glass header design restored
import React from 'react';
import { ConnectionStatus } from '../hooks/useWebSocket';
import { ConsciousnessMetadata } from '../types/Message';
import '../assets/styles/Header.css';

interface HeaderProps {
  connectionStatus: ConnectionStatus;
  onSidebarToggle: () => void;
  sessionId: string;
  voiceEnabled: boolean;
  voiceState: {
    isRecording: boolean;
    isProcessing: boolean;
    isSpeaking: boolean;
    error: string | null;
  };
  onVoiceToggle: () => void;
  activeProcesses: {
    imageGeneration: boolean;
    documentCreation: boolean;
    calendarProcessing: boolean;
    webSearching: boolean;
    thinking: boolean;
    wondering?: boolean;
    understanding?: boolean;
    experiencing?: boolean;
    crystallizing?: boolean;
    evolving?: boolean;
  };
  consciousnessState?: ConsciousnessMetadata;
}

const Header: React.FC<HeaderProps> = ({
  connectionStatus,
  onSidebarToggle,
  sessionId,
  voiceEnabled,
  voiceState,
  onVoiceToggle,
  activeProcesses,
  consciousnessState,
}) => {
  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case ConnectionStatus.CONNECTED:
        return '🟢';
      case ConnectionStatus.CONNECTING:
      case ConnectionStatus.RECONNECTING:
        return '🟡';
      case ConnectionStatus.ERROR:
      case ConnectionStatus.DISCONNECTED:
        return '🔴';
      default:
        return '⚪';
    }
  };

  // Simple activity indicator for active processes - minimal and clean
  const getSimpleActivityIndicator = () => {
    if (activeProcesses.thinking || activeProcesses.experiencing || activeProcesses.evolving) {
      return <span className="simple-activity">💭</span>;
    }
    if (activeProcesses.wondering) {
      return <span className="simple-activity">✨</span>;
    }
    if (activeProcesses.webSearching) {
      return <span className="simple-activity">🔍</span>;
    }
    if (activeProcesses.imageGeneration) {
      return <span className="simple-activity">🎨</span>;
    }
    if (activeProcesses.documentCreation) {
      return <span className="simple-activity">📝</span>;
    }
    return null;
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="sidebar-toggle" onClick={onSidebarToggle}>
          <div className="hamburger">
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </div>
        </button>
        
        <div className="logo-section">
          <div className="logo">🧠</div>
          <div className="title-section">
            <h1 className="title">EmCog</h1>
            <p className="subtitle">Conscious AI</p>
          </div>
        </div>
      </div>
      
      <div className="header-center">
        {getSimpleActivityIndicator()}
      </div>
      
      <div className="header-right">
        <div className="system-controls">
          <div className="connection-status">
            <div className={`connection-dot ${connectionStatus === ConnectionStatus.CONNECTED ? 'connected' : 'disconnected'}`}></div>
            <span className="connection-text">{connectionStatus}</span>
          </div>
          
          <button 
            className={`voice-control-small ${voiceEnabled ? 'active' : ''} ${
              voiceState.isRecording ? 'recording' : ''
            } ${
              voiceState.isSpeaking ? 'speaking' : ''
            } ${
              voiceState.error ? 'error' : ''
            }`}
            onClick={onVoiceToggle}
            disabled={voiceState.error !== null}
            title={voiceState.error || 'Voice control'}
          >
            {voiceState.isRecording ? '🔴' : 
             voiceState.isProcessing ? '⏳' :
             voiceState.isSpeaking ? '🔊' :
             voiceEnabled ? '🎤' : '🔇'}
          </button>
          
          <div className="capabilities-indicator" title="AI Capabilities Active">
            ⚡
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;